// src/components/circles/TalkAboutThis.jsx
//
// The point of a circle.
//
// Circles used to be a place to post: group reflections, comments, reactions,
// a question of the day answered in a text box. That made the app the venue
// for the conversation, which is both a teen social surface and the wrong
// place for it. These are people who already know each other and see each
// other.
//
// So the app doesn't host the conversation. It hands the circle the same two
// or three questions and gets out of the way. There is deliberately no answer
// box: the intent is that this gets talked about in person, side by side,
// which is the same thing Module 6 teaches.

import { MessageSquare, Users2 } from 'lucide-react';
import { talkPromptsFor } from '../../data/circleTalkPrompts';

export default function TalkAboutThis({ circle, now }) {
  const { moduleId, prompts } = talkPromptsFor(circle?.settings?.currentModule, now ?? Date.now());
  if (!prompts.length) return null;

  return (
    <section
      aria-labelledby="circle-talk-title"
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
          <MessageSquare aria-hidden="true" className="w-5 h-5 text-purple-300" />
        </div>
        <div className="min-w-0">
          <h2 id="circle-talk-title" className="text-lg font-bold text-white">Talk about this</h2>
          <p className="text-sm text-slate-300 mt-1">
            {moduleId ? `From Module ${moduleId}` : 'This week'}: the same questions for everyone in{' '}
            {circle?.name || 'your circle'}.
          </p>
        </div>
      </div>

      <ul className="space-y-3 mb-5">
        {prompts.map((p) => (
          <li key={p.prompt} className="p-4 bg-slate-900/40 rounded-xl border border-white/10">
            <p className="text-slate-100">{p.prompt}</p>
          </li>
        ))}
      </ul>

      <div className="flex items-start gap-3 p-4 bg-teal-500/10 border border-teal-400/30 rounded-xl">
        <Users2 aria-hidden="true" className="w-5 h-5 text-teal-300 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-teal-50">
          There&apos;s nowhere to type an answer here, and that&apos;s on purpose. Bring these to each other next
          time you&apos;re in the same place: walking somewhere, waiting around, on the way home. This kind of
          conversation goes better side by side than typed.
        </p>
      </div>
    </section>
  );
}
